import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useAssetJobFilterAuthor from "domains/assets/hooks/useAssetAuthor";
import useAssetSort from "domains/assets/hooks/useAssetSort";
import HorizontalScrollable from "domains/commons/components/HorizontalScrollable";
import usePersistedState, {
  PersistedStateKey,
} from "domains/commons/hooks/usePersistedState";
import FileManager from "domains/file-manager/components/FileManager";
import HeaderFilterAuthor from "domains/file-manager/components/FileManagerV2/FmHeaderFilterAuthor";
import HeaderFilterDate, {
  FmHeaderFilterDateProps,
} from "domains/file-manager/components/FileManagerV2/FmHeaderFilterDate";
import HeaderFilterTags from "domains/file-manager/components/FileManagerV2/FmHeaderFilterTags";
import { useFileModelHandler } from "domains/file-manager/hooks/useFileModelHandler";
import { FileModelType } from "domains/file-manager/interfaces";
import ModelListEmptyState from "domains/models/components/ModelList/ModelListEmptyState";
import { ModelCapability } from "domains/models/constants/capabilities";
import {
  MODEL_FAMILIES,
  ModelFamily,
  ModelTag,
} from "domains/models/constants/tags";
import useAllModels from "domains/models/hooks/useAllModels";
import useFrontFiltersToBack from "domains/models/hooks/useFrontFiltersToBack";
import {
  getIsModelFluxDev,
  getIsModelFluxPro,
  getIsModelFluxPro1_1,
  getIsModelFluxPro1_1Ultra,
  getIsModelGptImage,
} from "domains/models/utils";
import {
  getApiCapabilities,
  getApiCapabilitiesFromFilter,
} from "domains/models/utils/capabilities";
import { TOP_BAR_HEIGHT } from "domains/navigation/constants/TopBar";
import FilterCollections from "domains/search/components/SearchModal/FilterCollections";
import FilterModelBases, {
  ModelBase,
} from "domains/search/components/SearchModal/FilterModelBases";
import FilterModelCapabilities from "domains/search/components/SearchModal/FilterModelCapabilities";
import FilterModels from "domains/search/components/SearchModal/FilterModels";
import FilterModelType, {
  ModelType,
} from "domains/search/components/SearchModal/FilterModelType";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import Icon from "domains/ui/components/Icon";
import MenuListMultipleChoice from "domains/ui/components/Menu/MenuListMultipleChoice";
import ScenarioInput from "domains/ui/components/ScenarioInput";
import { GetModelsByModelIdApiResponse } from "infra/api/generated/api";
import _ from "lodash";

import { Box, HStack } from "@chakra-ui/react";

export interface ModelListProps {
  variant:
    | "image"
    | "canvas"
    | "canvas-live"
    | "texture"
    | "3d"
    | "video"
    | "video-upscale"
    | "all";
  onOpen: (file: FileModelType) => void;
  isMultiSelectable?: boolean;
  isInModal?: boolean;
  disableNotReady?: boolean;
}

export default React.memo(function ModelList({
  variant,
  onOpen,
  isMultiSelectable,
  isInModal,
  disableNotReady,
}: ModelListProps) {
  const isPersistedState = ![
    "texture",
    "video",
    "3d",
    "video-upscale",
  ].includes(variant ?? "");
  const isCanvas = variant === "canvas" || variant === "canvas-live";
  const { selectedTeam, selectedProject } = useTeamContext();
  const scrollRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState<string>("");
  const [families, setFamilies] = usePersistedState<ModelFamily[]>(
    isPersistedState ? PersistedStateKey.MODELS_FILTER_FAMILIES : undefined,
    {
      defaultValue: [],
    }
  );
  const [type, setType] = usePersistedState<ModelType | undefined>(
    isPersistedState ? PersistedStateKey.MODELS_FILTER_TYPES : undefined,
    {
      defaultValue: undefined,
    }
  );
  const [dateRange, setDateRange] = useState<
    FmHeaderFilterDateProps["value"] | undefined
  >();
  const { authorQueryArgs, fmHeaderFilterAuthorProps } =
    useAssetJobFilterAuthor({ avoidPersistedState: true });
  const [statuses, setStatuses] = usePersistedState<
    GetModelsByModelIdApiResponse["model"]["status"][]
  >(isPersistedState ? PersistedStateKey.MODELS_FILTER_STATUSES : undefined, {
    defaultValue: [],
  });
  const [tags, setTags] = usePersistedState<ModelTag[]>(
    isPersistedState ? PersistedStateKey.MODELS_FILTER_TAGS : undefined,
    {
      defaultValue: [],
    }
  );
  const [collectionIds, setCollectionIds] = usePersistedState<string[]>(
    isPersistedState ? PersistedStateKey.MODELS_FILTER_COLLECTIONS : undefined,
    {
      defaultValue: [],
    }
  );
  const [conceptIds, setConceptIds] = usePersistedState<string[]>(
    isPersistedState ? PersistedStateKey.MODELS_FILTER_CONCEPT_IDS : undefined,
    {
      defaultValue: [],
    }
  );
  const [bases, setBases] = usePersistedState<ModelBase[]>(
    isPersistedState ? PersistedStateKey.MODELS_FILTER_BASES : undefined,
    {
      defaultValue: [],
    }
  );
  const [capabilities, setCapabilities] = usePersistedState<ModelCapability[]>(
    isPersistedState ? PersistedStateKey.MODELS_FILTER_CAPABILITIES : undefined,
    {
      defaultValue: [],
    }
  );
  const [privacy, setPrivacy] = useState<
    GetModelsByModelIdApiResponse["model"]["privacy"][]
  >([]);
  const [facets, setFacets] = useState<{
    tags: Record<string, number>;
    collectionIds: Record<string, number>;
    types: Record<string, number>;
    conceptIds: Record<string, number>;
    authorIds: Record<string, number>;
  }>({
    tags: {},
    collectionIds: {},
    types: {},
    conceptIds: {},
    authorIds: {},
  });
  const { allAssetsSortArgs, assetFileManagerSortOptions } = useAssetSort();

  const fmHeaderFilterDateProps = useMemo(
    () => ({ value: dateRange, onChange: setDateRange }),
    [dateRange, setDateRange]
  );

  const authorsFilter = useMemo(() => {
    return authorQueryArgs?.authorId ? [authorQueryArgs.authorId] : undefined;
  }, [authorQueryArgs?.authorId]);

  const isFiltered = !!(
    families.length > 0 ||
    type !== undefined ||
    statuses.length > 0 ||
    tags.length > 0 ||
    collectionIds.length > 0 ||
    conceptIds.length > 0 ||
    privacy.length > 0 ||
    search
  );
  const isMultiSelectEnabled =
    isMultiSelectable && _.isEqual(families, ["private"]);

  const customFilter = useCallback(
    (model: GetModelsByModelIdApiResponse["model"]) => {
      if (
        isCanvas &&
        (getIsModelFluxPro(model) ||
          getIsModelFluxPro1_1(model) ||
          getIsModelFluxPro1_1Ultra(model) ||
          getIsModelGptImage(model) ||
          (variant === "canvas-live" && getIsModelFluxDev(model)))
      ) {
        return false;
      }

      if (model.privacy === "unlisted" && !privacy.includes("unlisted")) {
        return false;
      }

      if (privacy.length && !privacy.includes(model.privacy)) {
        return false;
      }

      if (families.length === 0) {
        return true;
      }
      if (
        families.includes("private") &&
        model?.ownerId === selectedProject.id
      ) {
        return true;
      }

      const hasFoundation =
        families.length === 0 || families.includes("foundation");
      const hasPlatform =
        families.length === 0 || families.includes("platform");
      const tags = model.tags.map((tag) => tag.toLowerCase());
      const familyTags = [
        ...(hasFoundation ? ["foundation", "sc:foundation"] : []),
        ...(hasPlatform ? ["signature", "sc:signature"] : []),
      ];
      return _.intersection(tags, familyTags).length > 0;
    },
    [families, isCanvas, privacy, selectedProject.id, variant]
  );

  const { types: typesFilter, sources: sourcesFilter } = useFrontFiltersToBack({
    type,
    bases,
  });

  const capabilitiesFilter = useMemo(() => {
    if (variant !== "all" && capabilities.length === 0) {
      return getApiCapabilities(
        variant === "canvas" || variant === "canvas-live" ? "image" : variant
      );
    }

    return getApiCapabilitiesFromFilter(capabilities);
  }, [variant, capabilities]);

  const tagsFilter = useMemo(() => {
    if (variant === "video-upscale") {
      return [...tags, "video-upscale"];
    }
    return tags;
  }, [tags, variant]);

  const excludeTags = useMemo(() => {
    if (variant === "video") {
      return ["video-upscale"];
    }
    return undefined;
  }, [variant]);

  const { files, isLoading, hasMore, loadMore } = useAllModels({
    isFeaturedPrioritized: true,
    typesFilter,
    tagsFilter,
    excludeTags,
    statusesFilter: statuses,
    collectionsFilter: collectionIds,
    conceptsFilter: conceptIds,
    sourcesFilter,
    authorsFilter,
    capabilitiesFilter,
    dateRange: fmHeaderFilterDateProps.value,
    privacyFilter: (() => {
      if (
        families.length > 0 &&
        _.difference(families, ["platform", "foundation"]).length === 0
      ) {
        return "public";
      } else if (families.length === 1 && families.includes("private")) {
        return "private";
      } else {
        return undefined;
      }
    })(),
    searchTerm: search,
    customFilter,
    sortDirection: allAssetsSortArgs.sortDirection as "asc" | "desc",
  });

  useEffect(() => {
    function calculateFacets() {
      setFacets((prevFacets) => {
        function getPreviousFacet(key: keyof typeof facets) {
          return Object.keys(prevFacets[key]).reduce((acc, item) => {
            acc[item] = 0;
            return acc;
          }, {} as Record<string, number>);
        }
        return files.reduce(
          (acc, file) => {
            file.meta.tags.forEach((tag) => {
              if (
                tag !== "sc:foundation" &&
                tag !== "sc:signature" &&
                tag !== "signature" &&
                tag !== "foundation"
              ) {
                acc.tags[tag] = (acc.tags[tag] ?? 0) + 1;
              }
            });
            file.meta.collectionIds.forEach((collectionId) => {
              acc.collectionIds[collectionId] =
                (acc.collectionIds[collectionId] ?? 0) + 1;
            });
            acc.types[file.meta.type] = (acc.types[file.meta.type] ?? 0) + 1;
            file.meta.concepts?.forEach((concept) => {
              acc.conceptIds[concept.modelId] =
                (acc.conceptIds[concept.modelId] ?? 0) + 1;
            });
            if (file.meta.authorId) {
              acc.authorIds[file.meta.authorId] =
                (acc.authorIds[file.meta.authorId] ?? 0) + 1;
            }
            return acc;
          },
          {
            tags: getPreviousFacet("tags"),
            collectionIds: getPreviousFacet("collectionIds"),
            types: getPreviousFacet("types"),
            conceptIds: getPreviousFacet("conceptIds"),
            authorIds: getPreviousFacet("authorIds"),
          }
        );
      });
    }
    const timeout = setTimeout(calculateFacets, 1_000);
    return () => clearTimeout(timeout);
  }, [files]);

  const modelFileHandler = useFileModelHandler({
    emptyState: (
      <Box p={4}>
        <ModelListEmptyState
          reason={isFiltered ? "no-search-results" : "no-models"}
        />
      </Box>
    ),
    disableNotReady,
    withLink: !isInModal,
  });

  // ----------------------------------

  return (
    <HStack
      ref={scrollRef}
      align="unset"
      overflow={isInModal ? "auto" : undefined}
      w="100%"
      h="100%"
      px={isInModal ? 5 : 9}
      bgColor="background.500"
      spacing={0}
    >
      <FileManager
        styleProps={{
          topbar: {
            top: isInModal ? "0px" : TOP_BAR_HEIGHT + "px",
          },
        }}
        files={files}
        isLoading={isLoading}
        hasMore={hasMore}
        loadMore={loadMore}
        topBarLeftContent={
          <>
            <ScenarioInput
              value={search}
              setValue={setSearch}
              placeholder="Search"
              leftComponent={<Icon id="Ui/Search" color="textTertiary" />}
              dataTestId="file-manager-selection-modal-search-input-model"
              containerProps={{
                w: "250px",
                flexShrink: 0,
              }}
            />

            <Box pos="relative" flex={9_999} alignSelf="stretch">
              <HorizontalScrollable
                childrenRef={filtersRef}
                getShouldAvoidScroll={(e) => {
                  return !!(
                    e.target instanceof HTMLElement &&
                    e.target.closest(".chakra-menu__menu-list")
                  );
                }}
                gradientColor="#121212"
                pos="absolute"
                top={0}
                left={0}
                right={0}
                bottom={0}
              >
                <HStack
                  ref={filtersRef}
                  sx={{
                    "&::-webkit-scrollbar": {
                      display: "none",
                    },
                    msOverflowStyle: "none",
                    scrollbarWidth: "none",
                  }}
                  align="center"
                  overflow="auto"
                  spacing={2}
                >
                  <MenuListMultipleChoice
                    label="Category"
                    options={MODEL_FAMILIES.map((family) => ({
                      value: family.id,
                      label: family.label,
                      dataTestId: `model-selection-family-${family.id}`,
                    }))}
                    selectedValues={families}
                    setSelectedValues={setFamilies}
                    withoutSearch
                    dataTestId="model-selection-family-category"
                  />
                  <FilterModelBases
                    value={bases}
                    setValue={setBases}
                    typesFacets={facets.types}
                  />
                  <FilterModelType
                    value={type}
                    setValue={setType}
                    models={files}
                  />
                  <FilterModelCapabilities
                    variant={
                      variant === "canvas" || variant === "canvas-live"
                        ? "image"
                        : variant
                    }
                    value={capabilities}
                    setValue={setCapabilities}
                    models={files}
                  />
                  {!!fmHeaderFilterAuthorProps && (
                    <HeaderFilterAuthor
                      {...fmHeaderFilterAuthorProps}
                      facets={facets.authorIds}
                    />
                  )}
                  <HeaderFilterDate {...fmHeaderFilterDateProps} />
                  <HeaderFilterTags
                    setValue={setTags}
                    value={tags}
                    facets={facets.tags}
                  />
                  <FilterCollections
                    value={collectionIds}
                    setValue={setCollectionIds}
                    facets={facets.collectionIds}
                  />
                  <FilterModels
                    label="Components"
                    value={conceptIds}
                    setValue={setConceptIds}
                    facets={facets.conceptIds}
                  />
                  <MenuListMultipleChoice
                    label="Status"
                    options={[
                      {
                        value: "trained",
                        label: "Ready",
                      },
                      {
                        value: "training",
                        label: "Training",
                      },
                      {
                        value: "new",
                        label: "Draft",
                      },
                    ]}
                    selectedValues={statuses}
                    setSelectedValues={setStatuses}
                    withoutSearch
                  />
                  {selectedTeam.isPublic && (
                    <MenuListMultipleChoice
                      label="Privacy"
                      options={[
                        {
                          value: "public",
                          label: "Public",
                        },
                        {
                          value: "private",
                          label: "Private",
                        },
                        {
                          value: "unlisted",
                          label: "Unlisted",
                        },
                      ]}
                      selectedValues={privacy}
                      setSelectedValues={setPrivacy}
                      withoutSearch
                    />
                  )}
                </HStack>
              </HorizontalScrollable>
            </Box>
          </>
        }
        options={{
          cacheKey: PersistedStateKey.FILE_MANAGER_MODEL,
          canSelect: isMultiSelectEnabled,
          canChangeView: false,
          gridView: "fill",
          canChangeNumberOfColumns: true,
          numberOfColumns: 6,
          showFileNames: "always",
          ...assetFileManagerSortOptions,
        }}
        fileHandlers={{
          model: {
            ...modelFileHandler,
            onOpen,
          },
        }}
        scrollRef={isInModal ? scrollRef : undefined}
      />
    </HStack>
  );
});
